/**
 * PATH: packages/ui/src/kanban/hooks/useBoardDefaults.ts
 * FILE_ID: UI-KANBAN-HOOK-BOARD-DEFAULTS-001
 * PURPOSE: Per-(user, org, category) "default board" pointers — the
 *          generalisation of `useBoardHome` to N workflow categories
 *          (HOME / JOB / QUOTE / FINANCE / EMAIL).
 *
 * FEAT-2026-05-25-BOARD-DEFAULTS-PER-CATEGORY. Operator follow-up
 *   (Mark Newman, Newmark) after FEAT-2026-05-24-KANBAN-BOARD-HOME:
 *
 *     "yeah but.. it shold hav eother cagtegorisations toooo.....
 *      llike quote job home finance... etc"
 *
 * SHAPE
 *   `useBoardDefaults({api, organisationId})` returns:
 *     {
 *       defaults:       BoardDefaultsBag,     // every category enumerated
 *       isLoading:      boolean,
 *       setDefault:     (category, boardId) => void,  // optimistic
 *       clearDefault:   (category) => void,           // optimistic
 *       isDefault:      (category, boardId) => boolean,
 *       defaultBoardId: (category) => string | null,  // sugar
 *       error:          unknown,
 *     }
 *
 * CACHE KEY
 *   ['kanban', 'board-defaults', orgId]
 *
 * RELATIONSHIP TO useBoardHome
 *   `useBoardHome` is now a thin wrapper around `useBoardDefaults`
 *   reading the HOME slot. Both hooks share the SAME react-query
 *   cache key family — but they are deliberately distinct keys
 *   (`board-home` vs `board-defaults`) because legacy host bundles
 *   that only know about `getBoardHome` would otherwise stale-fight
 *   with the new bag cache. The compat path is:
 *
 *     - New bundle + new BE: useBoardDefaults hits /board-defaults/,
 *       useBoardHome reads `defaults.HOME` from the shared cache via
 *       its delegation wrapper. ONE fetch per mount.
 *     - New bundle + old BE: useBoardDefaults degrades to HOME-only
 *       (falls back to getBoardHome) and the JOB/QUOTE/FINANCE/EMAIL
 *       slots stay null. Submenu still renders but only HOME toggles.
 *     - Old bundle + new BE: useBoardHome still works (qb!3079 keeps
 *       /board-home/ wired as a HOME-only shim). New categories are
 *       invisible to older bundles, which is correct.
 *
 * INVALIDATION CONTRACT
 *   Per-category mutations do an optimistic single-slot update in
 *   `onMutate`, run the PUT in the background, revert that slot
 *   only on error, and invalidate in `onSettled` to handle
 *   cross-device races.
 *
 * DESIGN NOTES
 *   - localStorage MIRROR is a JSON blob of the full bag at
 *     `kanban:board-defaults:v1:<orgId>`. Same `kanban:` prefix
 *     family as the home + pins mirrors so session-hygiene covers
 *     it for free.
 *   - Graceful degradation: if the host bundle predates
 *     `getBoardDefaults`/`setBoardDefault` but DOES have
 *     `getBoardHome`/`setBoardHome`, the hook silently falls
 *     through to the home-only path (every non-HOME category null).
 *     If neither pair exists (legacy-legacy), the hook is
 *     localStorage-only + no-op writes.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback } from 'react'

import type { KanbanApi } from '../api'
import {
  BOARD_DEFAULT_CATEGORIES,
  type BoardDefaultCategory,
  type BoardDefaultsBag,
} from '../api'

// =============================================================================
// localStorage mirror — JSON blob of the full bag, single key per org.
// Symmetric with `useBoardHome`'s single-id mirror; same `kanban:`
// prefix family so the session-hygiene wipe rule (`/^kanban:/`)
// covers it for free.
// =============================================================================

const DEFAULTS_PREFIX = 'kanban:board-defaults:v1:'

const EMPTY_BAG: BoardDefaultsBag = {
  HOME: null,
  JOB: null,
  QUOTE: null,
  FINANCE: null,
  EMAIL: null,
  // 6th category added 2026-05-25 follow-up — operator wanted full
  // hub coverage (less QDrive / Calendar; Bills + Invoices already
  // merged under FINANCE). See `BoardDefaultCategory` in ../api.ts
  // for the full rationale.
  BUILDER: null,
}

function defaultsKey(organisationId: number): string {
  return `${DEFAULTS_PREFIX}${organisationId}`
}

function readMirror(organisationId: number): BoardDefaultsBag {
  if (typeof window === 'undefined') return EMPTY_BAG
  try {
    const raw = window.localStorage.getItem(defaultsKey(organisationId))
    if (!raw) return EMPTY_BAG
    const parsed = JSON.parse(raw) as Partial<
      Record<BoardDefaultCategory, string | null | undefined>
    >
    // Normalise — every category enumerated, anything missing /
    // unparseable defaults to null so consumers can render every
    // slot without a presence check.
    return {
      HOME: typeof parsed.HOME === 'string' && parsed.HOME ? parsed.HOME : null,
      JOB: typeof parsed.JOB === 'string' && parsed.JOB ? parsed.JOB : null,
      QUOTE:
        typeof parsed.QUOTE === 'string' && parsed.QUOTE ? parsed.QUOTE : null,
      FINANCE:
        typeof parsed.FINANCE === 'string' && parsed.FINANCE
          ? parsed.FINANCE
          : null,
      EMAIL:
        typeof parsed.EMAIL === 'string' && parsed.EMAIL ? parsed.EMAIL : null,
      BUILDER:
        typeof parsed.BUILDER === 'string' && parsed.BUILDER
          ? parsed.BUILDER
          : null,
    }
  } catch {
    return EMPTY_BAG
  }
}

function writeMirror(organisationId: number, bag: BoardDefaultsBag): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(defaultsKey(organisationId), JSON.stringify(bag))
  } catch {
    // Quota / private mode — non-fatal. In-memory cache still valid.
  }
}

// =============================================================================
// Query key
// =============================================================================

export function boardDefaultsQueryKey(
  organisationId: number,
): readonly ['kanban', 'board-defaults', number] {
  return ['kanban', 'board-defaults', organisationId] as const
}

// =============================================================================
// Hook
// =============================================================================

export interface UseBoardDefaultsOptions {
  api: KanbanApi
  /** Master Quotech org id the rail is currently scoped to. For
   *  Newmark that's 6, NOT the sam-api local org id (20). The hook
   *  is skipped when `undefined`. */
  organisationId: number | undefined
}

export interface UseBoardDefaultsResult {
  /** Full bag — every category enumerated, unset slots are null. */
  defaults: BoardDefaultsBag
  isLoading: boolean
  /** Set the default for ONE category. Optimistic — that slot flips
   *  synchronously, the PUT runs in the background. Reverts on error
   *  (e.g. server rejected because the operator lost access to the
   *  board between selecting it and the round-trip). Other category
   *  slots are untouched. */
  setDefault: (category: BoardDefaultCategory, boardId: string) => void
  /** Clear the default for ONE category. Optimistic. Other slots
   *  untouched. */
  clearDefault: (category: BoardDefaultCategory) => void
  /** "Is THIS board the default for THIS category?" — saves the
   *  consumer from repeating `defaults[c] === board.id`. */
  isDefault: (category: BoardDefaultCategory, boardId: string) => boolean
  /** Sugar for the auto-resume effect: "what is THIS category's
   *  default id?" — equivalent to `defaults[category]`. */
  defaultBoardId: (category: BoardDefaultCategory) => string | null
  /** Surface server errors so the host can toast them on persistent
   *  failure (most likely an RBAC 400 — the operator just lost
   *  visibility to the board mid-session). */
  error: unknown
}

export function useBoardDefaults(
  opts: UseBoardDefaultsOptions,
): UseBoardDefaultsResult {
  const { api, organisationId } = opts
  const qc = useQueryClient()

  // Capability detection — three tiers:
  //   1. Full per-category API on both ends (new bundle + new BE).
  //   2. HOME-only legacy API (new bundle + old BE, OR an older
  //      tenant host that hasn't lifted to the new methods yet).
  //   3. No server methods at all — pure localStorage.
  const hasPerCategorySupport =
    typeof api?.getBoardDefaults === 'function'
    && typeof api?.setBoardDefault === 'function'
  const hasHomeOnlySupport =
    typeof api?.getBoardHome === 'function'
    && typeof api?.setBoardHome === 'function'

  const queryKey: readonly unknown[] =
    organisationId !== undefined
      ? boardDefaultsQueryKey(organisationId)
      : (['kanban', 'board-defaults', '__no-org__'] as const)

  const query = useQuery<BoardDefaultsBag>({
    queryKey,
    queryFn: async () => {
      if (organisationId === undefined) return EMPTY_BAG
      if (hasPerCategorySupport) {
        const bag = await api.getBoardDefaults!(organisationId)
        writeMirror(organisationId, bag)
        return bag
      }
      if (hasHomeOnlySupport) {
        // Compat path — only HOME is known to the BE; every other
        // category stays null. The submenu UI for non-HOME
        // categories is hidden in this state (`hasPerCategorySupport`
        // controls visibility — same pattern useBoardHome uses).
        const homeId = await api.getBoardHome!(organisationId)
        const compatBag: BoardDefaultsBag = { ...EMPTY_BAG, HOME: homeId }
        writeMirror(organisationId, compatBag)
        return compatBag
      }
      // Legacy-legacy bundle. localStorage is the only source.
      return readMirror(organisationId)
    },
    placeholderData:
      organisationId !== undefined ? readMirror(organisationId) : EMPTY_BAG,
    staleTime: 5 * 60 * 1000,
    refetchInterval: false,
    enabled: organisationId !== undefined,
  })

  // ---------------- mutation ----------------
  //
  // Per-category PUT: atomically upserts ONE slot. The optimistic
  // update flips just that slot in the bag, leaving the others
  // alone. Same race-handling pattern as useBoardHome (invalidate
  // on settled) extended to the bag shape.
  const setMutation = useMutation({
    mutationFn: async (
      vars: { category: BoardDefaultCategory; next: string | null },
    ) => {
      if (organisationId === undefined) return vars.next
      if (hasPerCategorySupport) {
        return api.setBoardDefault!(organisationId, vars.category, vars.next)
      }
      if (hasHomeOnlySupport && vars.category === 'HOME') {
        // Legacy BE that only knows HOME — writes still work for
        // that one category. Non-HOME writes silently fall through
        // to the no-op branch below.
        return api.setBoardHome!(organisationId, vars.next)
      }
      // Legacy-legacy OR non-HOME write to a HOME-only BE — record
      // in localStorage only so a future bundle/BE upgrade picks up
      // the pending preference without crashing.
      const bag = readMirror(organisationId)
      const nextBag = { ...bag, [vars.category]: vars.next ?? null }
      writeMirror(organisationId, nextBag)
      return vars.next
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey })
      const previous =
        qc.getQueryData<BoardDefaultsBag>(queryKey) ?? EMPTY_BAG
      const next = { ...previous, [vars.category]: vars.next ?? null }
      qc.setQueryData(queryKey, next)
      if (organisationId !== undefined) writeMirror(organisationId, next)
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx && 'previous' in ctx) {
        const prev = (ctx as { previous: BoardDefaultsBag }).previous
        qc.setQueryData(queryKey, prev)
        if (organisationId !== undefined) writeMirror(organisationId, prev)
      }
    },
    onSuccess: (canonical, vars) => {
      const current = qc.getQueryData<BoardDefaultsBag>(queryKey) ?? EMPTY_BAG
      const merged = { ...current, [vars.category]: canonical ?? null }
      qc.setQueryData(queryKey, merged)
      if (organisationId !== undefined) writeMirror(organisationId, merged)
    },
    onSettled: () => {
      // Belt-and-braces against cross-device races on the same
      // category. The single-key bag means one invalidation covers
      // every category.
      void qc.invalidateQueries({ queryKey })
    },
  })

  const setDefault = useCallback(
    (category: BoardDefaultCategory, boardId: string) =>
      setMutation.mutate({ category, next: boardId }),
    [setMutation],
  )
  const clearDefault = useCallback(
    (category: BoardDefaultCategory) =>
      setMutation.mutate({ category, next: null }),
    [setMutation],
  )

  const defaults = query.data ?? EMPTY_BAG
  const isDefault = useCallback(
    (category: BoardDefaultCategory, boardId: string) =>
      defaults[category] === boardId,
    [defaults],
  )
  const defaultBoardId = useCallback(
    (category: BoardDefaultCategory) => defaults[category],
    [defaults],
  )

  return {
    defaults,
    isLoading: query.isLoading,
    setDefault,
    clearDefault,
    isDefault,
    defaultBoardId,
    error: query.error,
  }
}

// Re-export the category constants so consumers can `import {
// useBoardDefaults, BOARD_DEFAULT_CATEGORIES } from '@quotech/ui/kanban'`
// without learning where the enum lives.
export { BOARD_DEFAULT_CATEGORIES }
export type { BoardDefaultCategory, BoardDefaultsBag }
